import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Drawer, List, ListItem, ListItemIcon, ListItemText,
    AppBar, Toolbar, Typography, IconButton, Button, Chip,
    useMediaQuery, useTheme,
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import CategoryIcon from '@mui/icons-material/Category';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import useAuth from '../../hooks/useAuth';

const DRAWER_WIDTH = 260;

const menuItems = [
    { text: 'Offres', icon: <WorkIcon />, path: '/director/offres' },
    { text: 'Demandes', icon: <PeopleIcon />, path: '/director/candidatures' },
    { text: 'Filières', icon: <CategoryIcon />, path: '/director/filieres' },
    { text: 'Modules', icon: <SchoolIcon />, path: '/director/modules' },
    { text: 'Paramètres ISTA', icon: <SettingsIcon />, path: '/director/parametres' },
    { text: 'Mon Compte', icon: <AccountCircleIcon />, path: '/director/compte' },
];

export default function DirectorLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, getUserIsta } = useAuth();
    const ista = getUserIsta();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const drawerContent = (
        <>
            <Toolbar />
            <List sx={{ mt: 2, px: 1.5 }}>
                {menuItems.map(item => {
                    const active = location.pathname.startsWith(item.path);
                    return (
                        <ListItem
                            key={item.path}
                            component={Link} to={item.path}
                            onClick={() => isMobile && setMobileOpen(false)}
                            sx={{
                                borderRadius: '12px',
                                mb: 1,
                                transition: 'all 0.2s',
                                color: active ? '#1b263b' : '#64748b',
                                bgcolor: active ? 'rgba(27, 38, 59, 0.05)' : 'transparent',
                                '&:hover': {
                                    bgcolor: 'rgba(27, 38, 59, 0.08)',
                                    color: '#1b263b',
                                },
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(27, 38, 59, 0.08)',
                                    color: '#1b263b',
                                    '& .MuiListItemIcon-root': { color: '#1b263b' }
                                },
                            }}
                            selected={active}
                        >
                            <ListItemIcon sx={{
                                color: active ? '#1b263b' : '#94a3b8',
                                minWidth: 40,
                                transition: 'all 0.2s'
                            }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{
                                    fontWeight: active ? 700 : 500,
                                    fontSize: '0.95rem'
                                }}
                            />
                            {active && (
                                <Box sx={{
                                    width: 4,
                                    height: 20,
                                    bgcolor: '#1b263b',
                                    borderRadius: 2,
                                    position: 'absolute',
                                    right: 8
                                }} />
                            )}
                        </ListItem>
                    );
                })}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                background: 'linear-gradient(90deg, #1b263b 0%, #415a77 100%)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            }}>
                <Toolbar sx={{ px: { xs: 1, sm: 3 } }}>
                    {isMobile && (
                        <IconButton color="inherit" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 1 }}>
                            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
                        </IconButton>
                    )}
                    <Typography variant="h6" noWrap sx={{
                        flexGrow: 1,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        letterSpacing: '-0.02em',
                        fontSize: { xs: '0.95rem', sm: '1.25rem' },
                    }}>
                        <Box sx={{
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            borderRadius: '8px'
                        }}>
                            <SchoolIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>OFPPT — RH</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>OFPPT</Box>
                        {ista && (
                            <Chip
                                label={ista.nom}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    color: 'white',
                                    fontWeight: 600,
                                    ml: 1,
                                    backdropFilter: 'blur(5px)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    display: { xs: 'none', lg: 'inline-flex' },
                                }}
                            />
                        )}
                    </Typography>
                    <Button component={Link} to="/" color="inherit" startIcon={<HomeIcon />}
                        sx={{
                            textTransform: 'none', mr: 1, borderRadius: 2,
                            display: { xs: 'none', sm: 'flex' },
                        }}>
                        Site
                    </Button>
                    <IconButton component={Link} to="/" color="inherit"
                        sx={{ display: { xs: 'flex', sm: 'none' }, mr: 0.5 }}>
                        <HomeIcon fontSize="small" />
                    </IconButton>
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: 'rgba(255,255,255,0.1)',
                        px: 2,
                        py: 0.5,
                        borderRadius: 10,
                        ml: 1
                    }}>
                        <AccountCircleIcon fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {user?.prenom} {user?.nom}
                        </Typography>
                    </Box>
                    <IconButton color="inherit" onClick={handleLogout} sx={{ ml: { xs: 0, sm: 1 } }}><LogoutIcon /></IconButton>
                </Toolbar>
            </AppBar>

            {!isMobile && (
                <Drawer variant="permanent" sx={{
                    width: DRAWER_WIDTH, flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: '1px solid rgba(0,0,0,0.06)',
                        bgcolor: '#ffffff'
                    },
                }}>
                    {drawerContent}
                </Drawer>
            )}

            {isMobile && (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            borderRight: '1px solid rgba(0,0,0,0.06)',
                            bgcolor: '#ffffff'
                        },
                    }}
                    ModalProps={{ keepMounted: true }}
                >
                    {drawerContent}
                </Drawer>
            )}

            <Box component="main" sx={{
                flexGrow: 1,
                p: { xs: 2, sm: 3, md: 4 },
                bgcolor: '#f8fafc',
                minHeight: '100vh',
                width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
                overflow: 'hidden',
            }}>
                <Toolbar />
                <Outlet />
            </Box>
        </Box>

    );
}
